import { useState } from 'react';
import { Sparkles, Loader2, Plus, Github, ChevronDown, ChevronUp } from 'lucide-react';
import { useProjectDescGenerator } from '@/hooks/ai';
import { GitHubRepoSelector } from '@/components/github';
import { type GitHubRepo } from '@/hooks/useGitHubAuth';
import { cn } from '@/lib/utils';
import { inferFullTechStack } from '@/lib/languageMapping';

export interface GeneratedProject {
  title: string;
  description: string;
  techStack: string[];
  githubUrl: string;
}

interface AIProjectDescGeneratorProps {
  onAddProject?: (project: GeneratedProject) => void;
}

export function AIProjectDescGenerator({ onAddProject }: AIProjectDescGeneratorProps) {
  const { description, isLoading, error, generate, reset } = useProjectDescGenerator();
  const [formData, setFormData] = useState({
    projectName: '',
    techStack: '',
    overview: '',
    githubUrl: '',
  });
  const [isAdded, setIsAdded] = useState(false);
  const [showRepoSelector, setShowRepoSelector] = useState(false);

  const handleRepoSelect = (repo: GitHubRepo) => {
    // リポジトリの言語と名前から技術スタックを推定
    const inferredTechStack = inferFullTechStack(
      repo.language,
      repo.name
    );

    setFormData(prev => ({
      ...prev,
      projectName: prev.projectName || repo.name,
      githubUrl: repo.url,
      // 既存の入力がなければ推定した技術スタックを使用
      techStack: prev.techStack || inferredTechStack.join(', '),
    }));
    setShowRepoSelector(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAdded(false);
    await generate({
      projectName: formData.projectName,
      techStack: formData.techStack.split(',').map(s => s.trim()).filter(Boolean),
      overview: formData.overview,
    });
  };

  const handleAddProject = () => {
    if (description && onAddProject) {
      onAddProject({
        title: formData.projectName,
        description: description,
        techStack: formData.techStack.split(',').map(s => s.trim()).filter(Boolean),
        githubUrl: formData.githubUrl || '',
      });
      setIsAdded(true);
    }
  };

  const handleReset = () => {
    reset();
    setFormData({
      projectName: '',
      techStack: '',
      overview: '',
      githubUrl: '',
    });
    setIsAdded(false);
    setShowRepoSelector(false);
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI プロジェクト説明ジェネレーター</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium">プロジェクト名</label>
          <input
            type="text"
            value={formData.projectName}
            onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="ポートフォリオサイト"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">技術スタック（カンマ区切り）</label>
          <input
            type="text"
            value={formData.techStack}
            onChange={(e) => setFormData({ ...formData, techStack: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="React, TypeScript, Tailwind CSS"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">概要</label>
          <textarea
            value={formData.overview}
            onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            rows={3}
            placeholder="このプロジェクトの目的や特徴を簡単に記述"
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm font-medium">
              <span className="flex items-center gap-1">
                <Github className="h-4 w-4" />
                GitHub URL（任意）
              </span>
            </label>
            {onAddProject && (
              <button
                type="button"
                onClick={() => setShowRepoSelector(!showRepoSelector)}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                リポジトリから選択
                {showRepoSelector ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
            )}
          </div>

          {showRepoSelector && (
            <div className="mb-2">
              <GitHubRepoSelector
                onSelect={handleRepoSelect}
                selectedUrl={formData.githubUrl}
              />
            </div>
          )}

          <input
            type="url"
            value={formData.githubUrl}
            onChange={(e) => setFormData({ ...formData, githubUrl: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="https://github.com/username/repo"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground",
              isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                生成する
              </>
            )}
          </button>
          {description && (
            <button
              type="button"
              onClick={handleReset}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              リセット
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {description && (
        <div className="mt-4 rounded-md bg-secondary p-4">
          <h4 className="mb-2 text-sm font-medium">生成されたプロジェクト説明:</h4>
          <p className="text-sm text-muted-foreground">{description}</p>

          {onAddProject && (
            <div className="mt-4">
              {isAdded ? (
                <div className="inline-flex items-center gap-2 rounded-md bg-green-600/10 px-4 py-2 text-sm font-medium text-green-600">
                  <Plus className="h-4 w-4" />
                  プロジェクトに追加しました
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleAddProject}
                  className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
                >
                  <Plus className="h-4 w-4" />
                  プロジェクトに追加
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
